import { IQuestionModel } from '@care-portals/api-interfaces';
import { caProvinces, usStates } from '../../data';

const questionsModels: IQuestionModel[] = [
  {
    label: 'Cash Pay Care Coordinator Intake Form',
    educationalSlide: `
      Here at Momwell, we understand your time is precious. Please complete our online history form before your first appointment to ensure a smooth initial session. It will take <b> 10-20 minutes </b> to complete, but here’s the good news:
      <br />
      <ul>
      <li>You only need to complete this form once.</li>
      <li>Your information is completely confidential.</li>
      <li>The form saves automatically, so you can complete it at your own pace, in one sitting or multiple sessions.</li>
      </ul>
      `,
    field: 'momwellIntakeCashIntro',
    type: 'info',
    answer: {}
  },
  {
    label: 'Intake Information',
    field: 'momwellIntakeCashIntakeInformation',
    type: 'info',
    answer: {
      type: 'list',
      items: [
        {
          label: 'First Name',
          type: 'text',
          placeholder: 'Please enter your first name',
          name: 'firstName',
          required: true,
          halfWidth: true
        },
        {
          label: 'Last Name',
          type: 'text',
          placeholder: 'Please enter your last name',
          name: 'lastName',
          required: true,
          halfWidth: true
        },
        {
          label: 'Email',
          type: 'text',
          placeholder: 'Please enter your email',
          name: 'email',
          required: true,
          halfWidth: true
        },
        {
          label: 'Client Location:',
          type: 'single_select',
          level: 'child',
          field: 'clientLocation',
          halfWidth: false,
          options: [...usStates, ...caProvinces].map(s => ({
            key: 'clientLocation_' + s.code,
            value: s.province
          }))
        },
        {
          label: 'How did you hear about Momwell?',
          type: 'single_select',
          level: 'child',
          field: 'howDidYouHearAboutUs',
          halfWidth: false,
          options: [
            {
              key: 'howDidYouHearAboutUs_childinternetSearch',
              value: 'Internet Search'
            },
            {
              key: 'howDidYouHearAboutUs_friendOrFamily',
              value: 'Friend or Family'
            },
            {
              key: 'howDidYouHearAboutUs_doctorOrHealthcareProvider',
              value: 'Doctor or Healthcare Provider'
            },
            {
              key: 'howDidYouHearAboutUs_advertisement',
              value: 'Advertisement'
            },
            {
              key: 'howDidYouHearAboutUs_socialMedia',
              value: 'Social Media'
            },
            {
              key: 'howDidYouHearAboutUs_podcast',
              value: 'Podcast'
            },
            {
              key: 'howDidYouHearAboutUs_brandPartner',
              value: 'Brand Partner (Willow, Huckleberry, etc)'
            },
            {
              key: 'howDidYouHearAboutUs_other',
              value: 'Other (Please specify)',
              answer: {
                type: 'text',
                rows: 3,
                name: 'howDidYouHearAboutUsOtherDesc',
                placeholder: ''
              }
            }
          ]
        },
        {
          label: 'Reason for booking?',
          type: 'text',
          placeholder: '',
          rows: 4,
          name: 'reasonForBooking',
          required: true,
          halfWidth: false
        }
      ]
    }
  },
  {
    label: 'Basic Risk Assessment',
    field: 'momwellIntakeCashBasicRiskAssessment',
    type: 'info',
    answer: {
      type: 'list',
      items: [
        {
          label: 'Client Complexity Categorization',
          type: 'single_select',
          level: 'child',
          field: 'complexity',
          halfWidth: false,
          options: [
            {
              key: 'complexity_low',
              value: 'Low'
            },
            {
              key: 'complexity_medium',
              value: 'Medium'
            },
            {
              key: 'complexity_high',
              value: 'High'
            },
            {
              key: 'complexity_crisis',
              value: 'Crisis',
              answer: {
                type: 'text',
                rows: 3,
                name: 'complexity_crisisDesc',
                placeholder: ''
              }
            }
          ]
        }
      ]
    }
  },
  {
    label: 'Billing Information',
    field: 'momwellIntakeCashBillingInformation',
    type: 'info',
    answer: {
      type: 'list',
      items: [
        {
          label: 'Client informed services are cash pay and cost of services',
          field: 'clientInformedServicesAreCashPayAndCostOfServices',
          type: 'checkbox',
          name: 'clientInformedServicesAreCashPayAndCostOfServicesConsent'
        },
        {
          label: 'Reviewed  billing and invoicing procedures',
          field: 'reviewedBillingAndInvoicingProcedures',
          type: 'checkbox',
          name: 'reviewedBillingAndInvoicingProcedures'
        },
        {
          label: 'Reviewed Cancelation Policy',
          field: 'reviewedCancelationPolicy',
          type: 'checkbox',
          name: 'reviewedCancelationPolicyConsent'
        },
        {
          label: 'Reviewed how to add payment on file in client portal',
          field: 'reviewedHowToAddPaymentOnFileInClientPortal',
          type: 'checkbox',
          name: 'reviewedHowToAddPaymentOnFileInClientPortalConsent'
        }
      ]
    }
  },
  {
    label: 'Scheduling',
    field: 'momwellIntakeCashScheduling',
    type: 'info',
    answer: {
      type: 'list',
      items: [
        {
          label: 'Therapist Match',
          type: 'text',
          placeholder: '',
          name: 'therapistMatch',
          required: true,
          halfWidth: false
        },
        {
          label: 'Client Requested Therapist (if applicable)',
          type: 'text',
          placeholder: '',
          name: 'clientRequestedTherapist',
          required: false,
          halfWidth: false
        },
        {
          label: 'Therapist assigned to client in the EMR',
          field: 'therapistAssignedToClientInTheEMR',
          type: 'checkbox',
          name: 'therapistAssignedToClientInTheEMRConsent'
        },
        {
          label: 'Stage 2: Initial Assessment Booked',
          field: 'stage2InitialAssessmentBooked',
          type: 'select',
          answer: {
            type: 'single_select',
            options: [
              { key: 'yes', value: 'Yes' },
              { key: 'no', value: 'No' }
            ]
          }
        },
        {
          label: 'Reviewed Intake & Consent Forms Process',
          field: 'reviewedIntakeConsentFormsProcess',
          type: 'checkbox',
          name: 'reviewedIntakeConsentFormsProcessConsent'
        },
        {
          label: 'Booked ahead first 3 Stage 3 Ongoing Sessions',
          field: 'bookedAheadFirst3Stage3OngoingSessions',
          type: 'select',
          answer: {
            type: 'single_select',
            options: [
              { key: 'yes', value: 'Yes' },
              { key: 'no', value: 'No' }
            ]
          }
        }
      ]
    }
  },
  {
    label: 'Next Steps',
    field: 'momwellIntakeCashNextSteps',
    type: 'info',
    answer: {
      type: 'list',
      items: [
        {
          label: 'Confirm booking confirmation email received',
          field: 'confirmBookingConfirmationEmailReceived',
          type: 'checkbox',
          name: 'confirmBookingConfirmationEmailReceivedConsent'
        },
        {
          label: 'Confirm portal account created',
          field: 'confirmPortalAccountCreated',
          type: 'checkbox',
          name: 'confirmPortalAccountCreatedConsent'
        },
        {
          label: 'Review how to join a session',
          field: 'reviewHowToJoinASession',
          type: 'checkbox',
          name: 'reviewHowToJoinASessionConsent'
        },
        {
          label:
            'Provided Instructions for booking Stage 2 Initial Assessment if not done already',
          field:
            'providedInstructionsForBookingStage2InitialAssessmentIfNotDoneAlready',
          type: 'checkbox',
          name: 'providedInstructionsForBookingStage2InitialAssessmentIfNotDoneAlreadyConsent'
        }
      ]
    }
  },
  {
    label: 'Follow Up',
    field: 'momwellIntakeCashFollowUp',
    type: 'info',
    answer: {
      type: 'list',
      items: [
        {
          label:
            'Stage 2 was not booked during the consultation, what was the reason?',
          type: 'text',
          placeholder: '',
          name: 'stage2WasNotBookedDuringTheConsultationWhatWasTheReason',
          required: true,
          halfWidth: false,
          rows: 3
        },
        {
          label:
            'Set reminder in Monday.com tasks to call client in one week for follow up',
          field: 'setReminderInMondayComTasksToCallClientInOneWeekForFollowUp',
          type: 'checkbox',
          name: 'setReminderInMondayComTasksToCallClientInOneWeekForFollowUpConsent'
        }
      ]
    }
  },
  {
    label: 'NOTES',
    field: 'momwellIntakeCashNotes',
    type: 'info',
    answer: {
      type: 'list',
      items: [
        {
          label: '',
          type: 'text',
          placeholder: '',
          name: 'momwellIntakeCashNotesDesc',
          required: false,
          halfWidth: false,
          rows: 5
        }
      ]
    }
  }
];

export default questionsModels;
